import type { ThemeChoices } from "$/lib/types/General.types";
import { numberFormatter } from "$/lib/utils/functions/util.functions";
import { Fragment, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TextProps,
} from "recharts";
import { customColors } from "tailwind.config";
import Flexbox from "../../common/Flexbox";
import type { GraphFilterItemsProps } from "../GraphFilter/GraphFilterItem";
import CustomTooltip from "../LineGraph/CustomTooltip";

// const BarWithBorderRadius = (props: BarProps & { isVisible: boolean }) => {
//   const { fill, x, y, width, height, isVisible } = props;

//   if (
//     typeof x !== "number" ||
//     typeof y !== "number" ||
//     typeof width !== "number" ||
//     typeof height !== "number"
//   )
//     return <></>;

//   const radius = 2;

//   if (!isVisible) return <></>;

//   return (
//     <path
//       width={width}
//       radius={radius}
//       // d={(() => {
//       //   // Ensure all values are numbers and handle edge cases
//       //   const validX = typeof x === "number" ? x : 0;
//       //   const validY = typeof y === "number" ? y : 0;
//       //   const validWidth = typeof width === "number" ? width : 0;
//       //   const validHeight = typeof height === "number" ? height : 0;
//       //   const validRadius =
//       //     typeof radius === "number" && radius >= 0 ? radius : 0;

//       //   return `M${validX},${validY + validHeight}
//       // h${validWidth}
//       // a${validRadius},${validRadius} 0 0 1 -${validRadius},-${validRadius}
//       // v${validHeight - validRadius < 0 ? validHeight - validRadius : `-${validHeight - validRadius}`}
//       // a${validRadius},${validRadius} 0 0 1 ${validRadius},-${validRadius}
//       // h${validWidth - 2 * validRadius}
//       // a${validRadius},${validRadius} 0 0 1 ${validRadius},${validRadius}
//       // v${validHeight - validRadius}
//       // a${validRadius},${validRadius} 0 0 1 -${validRadius},${validRadius}
//       // z`;
//       // })()}
//       stroke="none"
//       fill={fill}
//       {...props}
//       // d={`M${x},${y + height} h${width} a${radius},${radius} 0 0 1 -${radius},-${radius} v${
//     />
//   );
// };

const VerticalLabel = (props: TextProps & { dataKey: string }) => {
  const { x, y, width, height, dataKey } = props as TextProps & {
    value: string;
    dataKey: string;
  };

  if (
    typeof x !== "number" ||
    typeof y !== "number" ||
    typeof width !== "number" ||
    typeof height !== "number"
  )
    return <></>;

  const labelX = x + width / 2;
  const labelY = y + height + 15;

  return (
    <text
      x={labelX}
      y={labelY}
      textAnchor="middle"
      fill={customColors.tertiary.DEFAULT}
      fontSize={10}
      transform={`rotate(-90, ${labelX + 5}, ${labelY - 5})`}
      dominantBaseline="middle"
    >
      {dataKey}
    </text>
  );
};

type Props = {
  data: unknown[];
  xDataKey: string;
  dataKeys: { key: string; color: string }[];
  yAxisLabel?: string;
  labelPosition?: string;
  isStacked?: boolean;
  unit?: string;
  graphVisibility?: GraphFilterItemsProps[];
  theme?: ThemeChoices;
  withLabel?: boolean;
  minHeight?: number;
};

const BarGraph = ({
  data,
  xDataKey,
  dataKeys,
  yAxisLabel,
  isStacked = false,
  unit = " Mwh",
  graphVisibility,
  theme = "blue",
  withLabel = true,
  labelPosition = "insideBottomLeft",
  minHeight = 100,
}: Props) => {
  const visibleKeys = useMemo(
    () =>
      graphVisibility
        ? graphVisibility.filter((item) => item.name !== "Tout")
        : dataKeys.map((item) => ({
            name: item.key,
            color: item.color,
            isVisible: true,
          })),
    [graphVisibility, dataKeys],
  );

  const hideKeys = useMemo(
    () =>
      visibleKeys.reduce<string[]>((acc, item) => {
        if (item.isVisible === false) return [...acc, item.name];

        return acc;
      }, []),
    [visibleKeys],
  );

  return (
    <Flexbox fullHeight fullWidth>
      {/* Chart for PEG Day Ahead */}
      {/* <h2>PEG Day Ahead</h2> */}
      <ResponsiveContainer
        width="100%"
        // height="100%"
        minHeight={minHeight}
        minWidth={600}
      >
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 10, bottom: 25 }}
          barCategoryGap={10}
          barGap={2}
        >
          <CartesianGrid stroke="#E2E2E2" horizontal={false} />

          <ReferenceLine
            y={0}
            stroke={customColors.tertiary.DEFAULT}
            strokeDasharray={5}
          />

          <XAxis
            dataKey={xDataKey}
            axisLine={false}
            tickMargin={10}
            fontSize={12}
            tickLine={false}
            orientation="top"
          />

          <YAxis
            label={{
              value: yAxisLabel,
              angle: -90,
              position: labelPosition,
            }}
            tickSize={2}
            width={85}
            fontWeight={500}
            fontSize={12}
            tickMargin={5}
            unit={unit}
            tickFormatter={numberFormatter}
          />

          <Tooltip
            content={
              <CustomTooltip unit={unit} theme={theme} hideKeys={hideKeys} />
            }
          />

          {visibleKeys.map(({ name, color, isVisible }) =>
            isVisible === undefined || isVisible ? (
              <Bar
                key={name}
                // shape={
                //   <BarWithBorderRadius
                //     dataKey={name}
                //     width={12}
                //     isVisible={isVisible === undefined || isVisible}
                //   />
                // }
                radius={[2, 2, 0, 0]}
                dataKey={name}
                stackId={isStacked ? "a" : undefined}
                stroke={color}
              >
                {isVisible &&
                  data.map((_, index) => (
                    <Fragment key={`cell-${index}`}>
                      <Cell fill={color} />
                      {withLabel && (
                        <LabelList content={<VerticalLabel dataKey={name} />} />
                      )}
                    </Fragment>
                  ))}
              </Bar>
            ) : (
              <></>
            ),
          )}
        </BarChart>

        {/* Chart for Spot Elec Base */}
      </ResponsiveContainer>
    </Flexbox>
  );
};

export default BarGraph;
